import { useEffect, useState } from "react";

/**
 * "App updates" in Settings, for the builds handed out outside Google Play (owner,
 * 2026-10-19: the remote debugging screen goes, an update button stays for outside the Play
 * Store). Native side: native/android/.../update/AppUpdatePlugin.java, which checks
 * the link fixed in the build (SliqueTV's Downloader link, or a white-label tenant's).
 *
 * Renders nothing where the plugin is absent: the website, and TV My Way, because
 * Google Play allows an app no self-update.
 */

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
}

const PLUGIN = "AppUpdate";

export function appUpdateCall<T>(method: string): Promise<T> | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true || typeof cap.nativePromise !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap.nativePromise(PLUGIN, method, {}) as Promise<T>;
}

interface Info {
  version: string;
  installedAt: number;
}

interface Check {
  published: boolean;
  available: boolean;
  publishedAt?: number;
  bytes?: number;
}

type Status =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "current" }
  | { kind: "available"; publishedAt?: number; bytes?: number }
  | { kind: "downloading" }
  | { kind: "installing" }
  | { kind: "error"; message: string };

function dayOf(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

export function AppUpdateCard() {
  const [info, setInfo] = useState<Info | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  useEffect(() => {
    void appUpdateCall<Info>("getInfo")
      ?.then(setInfo)
      .catch(() => setInfo(null));
  }, []);

  if (!info) return null;

  const check = async () => {
    setStatus({ kind: "checking" });
    try {
      const result = await appUpdateCall<Check>("check");
      if (!result || !result.published || !result.available) setStatus({ kind: "current" });
      else setStatus({ kind: "available", publishedAt: result.publishedAt, bytes: result.bytes });
    } catch (error) {
      setStatus({ kind: "error", message: messageOf(error, "Couldn't check for updates.") });
    }
  };

  const install = async () => {
    setStatus({ kind: "downloading" });
    try {
      await appUpdateCall("install");
      setStatus({ kind: "installing" });
    } catch (error) {
      setStatus({ kind: "error", message: messageOf(error, "The download failed.") });
    }
  };

  const busy = status.kind === "checking" || status.kind === "downloading";
  const note =
    status.kind === "checking"
      ? "Checking…"
      : status.kind === "current"
        ? "You have the latest version."
        : status.kind === "available"
          ? `A newer version is ready${status.publishedAt ? ` (${dayOf(status.publishedAt)})` : ""}${
              status.bytes && status.bytes > 0 ? `, ${Math.round(status.bytes / 1048576)} MB` : ""
            }. Your sources, profiles and recordings are kept.`
          : status.kind === "downloading"
            ? "Downloading…"
            : status.kind === "installing"
              ? "Downloaded. Android will ask you to install it."
              : status.kind === "error"
                ? status.message
                : null;

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">App updates</h2>
      <div className="settings-row">
        <span className="settings-row-label">Version</span>
        <span className="settings-row-value">
          {info.version || "Unknown"}
          {info.installedAt > 0 ? `, installed ${dayOf(info.installedAt)}` : ""}
        </span>
      </div>
      <div className="settings-row">
        <span className="settings-row-label">Updates</span>
        {status.kind === "available" ? (
          <button type="button" className="ui-btn ui-btn--primary" onClick={() => void install()}>
            Download and install
          </button>
        ) : (
          <button type="button" className="ui-btn" onClick={() => void check()} disabled={busy}>
            {status.kind === "downloading" ? "Downloading…" : "Check for updates"}
          </button>
        )}
      </div>
      {note && (
        <div className="settings-row">
          <span className="settings-row-hint" role="status">
            {note}
          </span>
        </div>
      )}
    </div>
  );
}
